/**
 * How a program's session ends — the difference between handing a child to an adult and
 * simply finishing a class.
 *
 * The after-school programs dismiss into a pickup line: a named adult collects the child,
 * the teacher records who, and the parent is told it happened. An enrichment class is not
 * that. Chess, piano, tennis and the math classes run for an hour inside the building the
 * children are already in; at the end the teacher marks the session done and they go back
 * to after-school or out to the same yard they arrived from. There is no adult to name, and
 * telling a parent their child "was picked up" would be false.
 *
 * So `pickup` is an allowlist of exactly the types that dismiss to an adult, and everything
 * else is a `class`. In that order a new enrichment type behaves correctly the day it is
 * created; the mistake worth preventing is an unfamiliar type quietly claiming to be a
 * supervised handover.
 */

export type AttendanceMode = 'pickup' | 'class';

/** `programs.type` values whose sessions end by releasing a child to a named adult. */
const PICKUP_RELEASE_TYPES: ReadonlySet<string> = new Set(['after-school', 'camp']);

/**
 * The mode for a `programs.type` value.
 *
 * A missing type resolves to `pickup`, not `class` — the opposite of the rule for an
 * unrecognized one. Absent means the caller could not read the program at all (a dropped
 * embed, a row mid-load), and the failure that matters there is a real after-school handover
 * that never reaches the parent. An extra card is recoverable; a withheld pickup is not.
 */
export function attendanceModeFor(programType: string | null | undefined): AttendanceMode {
  if (!programType) return 'pickup';
  return PICKUP_RELEASE_TYPES.has(programType.toLowerCase()) ? 'pickup' : 'class';
}

/**
 * True when this program dismisses a child to a named adult.
 *
 * Also the test for whether attendance belongs on a parent surface: a pickup is an event in
 * the family's day, whereas a class ending is the teacher's own bookkeeping.
 */
export function isPickupProgram(programType: string | null | undefined): boolean {
  return attendanceModeFor(programType) === 'pickup';
}
